import React, { useState, useEffect, useCallback } from 'react';
import { advReportsApi, projectApi } from '../services/api';
import { Button, Card, Select } from '../components/UI';
import { ChartBar, CurrencyDollar, Users, Package, Receipt, TrendUp, TrendDown, Scales } from '@phosphor-icons/react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmt = (v) => new Intl.NumberFormat('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

export default function AdvancedReportsPage() {
  const [tab, setTab] = useState('profitability');
  const [profitData, setProfitData] = useState([]);
  const [resourceData, setResourceData] = useState([]);
  const [payrollData, setPayrollData] = useState([]);
  const [invValuation, setInvValuation] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selProject, setSelProject] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const projRes = await projectApi.list();
      setProjects(projRes.data);
    } catch (e) { setError('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadTab = useCallback(async () => {
    try {
      if (tab === 'profitability') { const r = await advReportsApi.projectProfitability(); setProfitData(r.data); }
      else if (tab === 'resources') { const r = await advReportsApi.resourceUtilization(selProject ? { project_id: selProject } : {}); setResourceData(r.data); }
      else if (tab === 'payroll') { const r = await advReportsApi.payrollSummary(); setPayrollData(r.data); }
      else if (tab === 'inventory') { const r = await advReportsApi.inventoryValuation(); setInvValuation(r.data); }
    } catch (e) { setError('Failed to load report'); }
  }, [tab, selProject]);

  useEffect(() => { loadTab(); }, [loadTab]);

  const exportPDF = (title, headers, rows) => {
    const doc = new jsPDF('landscape');
    doc.setFontSize(16); doc.text(title, 14, 20);
    doc.setFontSize(8); doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 28);
    autoTable(doc, { startY: 32, head: [headers], body: rows, styles: { fontSize: 7 } });
    doc.save(`${title.replace(/\s/g, '_').toLowerCase()}.pdf`);
  };

  const totalProfit = profitData.reduce((s, p) => s + p.profit, 0);
  const totalCost = profitData.reduce((s, p) => s + p.total_cost, 0);
  const totalInvoiced = profitData.reduce((s, p) => s + p.total_invoiced, 0);

  const tabs = [
    { key: 'profitability', label: 'Project Profitability', icon: TrendUp },
    { key: 'resources', label: 'Resource Utilization', icon: Users },
    { key: 'payroll', label: 'Payroll Reports', icon: CurrencyDollar },
    { key: 'inventory', label: 'Inventory Valuation', icon: Package },
  ];

  return (
    <div className="space-y-4" data-testid="advanced-reports-page">
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A]" data-testid="reports-heading">Advanced Reports</h1>
        <p className="text-sm text-[#666]">Project profitability, resources, payroll & inventory analytics</p>
      </div>

      <div className="flex gap-1 border-b border-[#E0E0E0] overflow-x-auto" data-testid="report-tabs">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key ? 'border-[#1A1F36] text-[#1A1F36]' : 'border-transparent text-[#888] hover:text-[#555]'}`}
            data-testid={`tab-${t.key}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}

      {/* Project Profitability */}
      {tab === 'profitability' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-3 text-center">
              <p className="text-[10px] text-[#999] uppercase">Projects</p>
              <p className="text-xl font-bold text-[#0A0A0A]">{profitData.length}</p>
            </Card>
            <Card className="p-3 text-center">
              <p className="text-[10px] text-[#999] uppercase">Total Cost</p>
              <p className="text-xl font-bold text-[#FF3333]">{fmt(totalCost)}</p>
            </Card>
            <Card className="p-3 text-center">
              <p className="text-[10px] text-[#999] uppercase">Total Invoiced</p>
              <p className="text-xl font-bold text-[#002FA7]">{fmt(totalInvoiced)}</p>
            </Card>
            <Card className="p-3 text-center">
              <p className="text-[10px] text-[#999] uppercase">Total Profit</p>
              <p className={`text-xl font-bold ${totalProfit >= 0 ? 'text-[#00C853]' : 'text-[#FF3333]'}`}>{fmt(totalProfit)}</p>
            </Card>
          </div>
          <div className="flex justify-end">
            <Button size="sm" variant="secondary" onClick={() => exportPDF('Project Profitability', ['Project', 'Client', 'Contract', 'Cost', 'Invoiced', 'Profit', 'Margin%'], profitData.map(p => [p.project_name, p.client_name, fmt(p.contract_value), fmt(p.total_cost), fmt(p.total_invoiced), fmt(p.profit), p.margin_pct + '%']))} data-testid="export-profit-pdf">Export PDF</Button>
          </div>
          {profitData.length === 0 ? <Card className="p-8 text-center text-[#666]">No project data</Card> : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] text-[#999] uppercase border-b border-[#E0E0E0]">
                    <th className="px-4 py-2.5">Project</th><th className="px-4 py-2.5">Client</th><th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5 text-right">Contract</th><th className="px-4 py-2.5 text-right">Cost</th>
                    <th className="px-4 py-2.5 text-right">Invoiced</th><th className="px-4 py-2.5 text-right">Paid</th>
                    <th className="px-4 py-2.5 text-right">Profit</th><th className="px-4 py-2.5 text-right">Margin</th>
                  </tr></thead>
                  <tbody>
                    {profitData.map(p => (
                      <tr key={p.project_id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`profit-row-${p.project_id}`}>
                        <td className="px-4 py-2.5 font-medium text-[#0A0A0A]">{p.project_name}</td>
                        <td className="px-4 py-2.5 text-[#666]">{p.client_name}</td>
                        <td className="px-4 py-2.5"><span className="text-[10px] bg-[#E5E5E5] px-1.5 py-0.5 rounded">{p.status}</span></td>
                        <td className="px-4 py-2.5 text-right text-[#666]">{fmt(p.contract_value)}</td>
                        <td className="px-4 py-2.5 text-right text-[#FF3333]">{fmt(p.total_cost)}</td>
                        <td className="px-4 py-2.5 text-right text-[#002FA7]">{fmt(p.total_invoiced)}</td>
                        <td className="px-4 py-2.5 text-right text-[#00C853]">{fmt(p.total_paid)}</td>
                        <td className={`px-4 py-2.5 text-right font-bold ${p.profit >= 0 ? 'text-[#00C853]' : 'text-[#FF3333]'}`}>{fmt(p.profit)}</td>
                        <td className="px-4 py-2.5 text-right text-[#666]">{p.margin_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Resource Utilization */}
      {tab === 'resources' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" variant="secondary" onClick={() => exportPDF('Resource Utilization', ['Project', 'Workers', 'Hours', 'Mat Received', 'Mat Used'], resourceData.map(r => [r.project_name, r.total_workers, r.total_hours, r.material_received, r.material_used]))} data-testid="export-resource-pdf">Export PDF</Button>
          </div>
          {resourceData.length === 0 ? <Card className="p-8 text-center text-[#666]">No resource data</Card> : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {resourceData.map(r => (
                <Card key={r.project_id} className="p-4" data-testid={`resource-card-${r.project_id}`}>
                  <p className="font-semibold text-sm text-[#0A0A0A] mb-3">{r.project_name}</p>
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <div><p className="text-xs text-[#666]">Workers</p><p className="text-lg font-bold text-[#0A0A0A]">{r.total_workers}</p></div>
                    <div><p className="text-xs text-[#666]">Hours</p><p className="text-lg font-bold text-[#002FA7]">{r.total_hours}</p></div>
                    <div><p className="text-xs text-[#666]">Mat. Received</p><p className="text-lg font-bold text-[#00C853]">{r.material_received}</p></div>
                    <div><p className="text-xs text-[#666]">Mat. Used</p><p className="text-lg font-bold text-[#FF3333]">{r.material_used}</p></div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Payroll Reports */}
      {tab === 'payroll' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" variant="secondary" onClick={() => exportPDF('Payroll Summary', ['Period', 'Status', 'Employees', 'Gross', 'Deductions', 'Net'], payrollData.map(r => [`${r.period_month}/${r.period_year}`, r.status, r.employee_count, fmt(r.total_gross), fmt(r.total_deductions), fmt(r.total_net)]))} data-testid="export-payroll-pdf">Export PDF</Button>
          </div>
          {payrollData.length === 0 ? <Card className="p-8 text-center text-[#666]">No payroll data</Card> : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] text-[#999] uppercase border-b border-[#E0E0E0]">
                    <th className="px-4 py-2.5">Period</th><th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5 text-right">Employees</th><th className="px-4 py-2.5 text-right">Gross</th>
                    <th className="px-4 py-2.5 text-right">Deductions</th><th className="px-4 py-2.5 text-right">Net</th>
                  </tr></thead>
                  <tbody>
                    {payrollData.map(r => (
                      <tr key={r.id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`payroll-row-${r.id}`}>
                        <td className="px-4 py-2.5 font-medium text-[#0A0A0A]">{r.period_month}/{r.period_year}</td>
                        <td className="px-4 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 rounded ${r.status === 'processed' ? 'bg-[#E8F5E9] text-[#00C853]' : 'bg-[#E5E5E5] text-[#666]'}`}>{r.status}</span></td>
                        <td className="px-4 py-2.5 text-right text-[#666]">{r.employee_count}</td>
                        <td className="px-4 py-2.5 text-right text-[#0A0A0A]">{fmt(r.total_gross)}</td>
                        <td className="px-4 py-2.5 text-right text-[#FF3333]">{fmt(r.total_deductions)}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-[#00C853]">{fmt(r.total_net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Inventory Valuation */}
      {tab === 'inventory' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" variant="secondary" onClick={() => exportPDF('Inventory Valuation', ['Material', 'Warehouse', 'Qty In', 'Qty Out', 'Balance', 'Value'], invValuation.map(v => [v.material_name, v.warehouse_name, v.qty_in, v.qty_out, v.balance_qty, fmt(v.total_value)]))} data-testid="export-inv-pdf">Export PDF</Button>
          </div>
          {invValuation.length === 0 ? <Card className="p-8 text-center text-[#666]">No inventory data</Card> : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] text-[#999] uppercase border-b border-[#E0E0E0]">
                    <th className="px-4 py-2.5">Material</th><th className="px-4 py-2.5">Warehouse</th>
                    <th className="px-4 py-2.5 text-right">In</th><th className="px-4 py-2.5 text-right">Out</th>
                    <th className="px-4 py-2.5 text-right">Balance</th><th className="px-4 py-2.5 text-right">Value</th>
                  </tr></thead>
                  <tbody>
                    {invValuation.map((v, i) => (
                      <tr key={i} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`inv-val-row-${i}`}>
                        <td className="px-4 py-2.5 font-medium text-[#0A0A0A]">{v.material_name}</td>
                        <td className="px-4 py-2.5 text-[#666]">{v.warehouse_name}</td>
                        <td className="px-4 py-2.5 text-right text-[#00C853]">{v.qty_in}</td>
                        <td className="px-4 py-2.5 text-right text-[#FF3333]">{v.qty_out}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-[#002FA7]">{v.balance_qty}</td>
                        <td className="px-4 py-2.5 text-right text-[#666]">{fmt(v.total_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
